import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState
} from 'react';
import { useHistory } from 'react-router-dom';

import './WebsiteNavigation.scss';

export const WebsiteNavigationContext = createContext({
	canGoBack: false,
	goBack: () => {}
});

const getLocationId = location =>
	location.key ||
	`${location.pathname || ''}${location.search || ''}${location.hash || ''}`;

const isPlainLeftClick = event =>
	event.button === 0 &&
	!event.altKey &&
	!event.ctrlKey &&
	!event.metaKey &&
	!event.shiftKey;

/**
 * Keeps a session-local stack of routes reached through CorrGuard.
 *
 * A fresh browser load deliberately starts with an empty website history,
 * even when the browser itself can go back to another origin. Internal route
 * pushes are added to this stack, while POP actions move within it.
 */
export const WebsiteNavigationProvider = ({ children }) => {
	const history = useHistory();
	const routeStackRef = useRef({
		entries: [getLocationId(history.location)],
		index: 0
	});
	const [canGoBack, setCanGoBack] = useState(false);

	useEffect(() => {
		return history.listen((location, action) => {
			const routeStack = routeStackRef.current;
			const locationId = getLocationId(location);

			if (action === 'PUSH') {
				routeStack.entries = routeStack.entries.slice(0, routeStack.index + 1);
				routeStack.entries.push(locationId);
				routeStack.index += 1;
			} else if (action === 'POP') {
				const existingIndex = routeStack.entries.indexOf(locationId);

				if (existingIndex === -1) {
					routeStack.entries = [locationId];
					routeStack.index = 0;
				} else {
					routeStack.index = existingIndex;
				}
			} else {
				routeStack.entries[routeStack.index] = locationId;
			}

			setCanGoBack(routeStack.index > 0);
		});
	}, [history]);

	/*
	 * Some older screens still use ordinary same-origin <a> elements. Route
	 * those links through React Router so the current screen remains in the
	 * website stack and returning to it does not require a document reload.
	 */
	useEffect(() => {
		const handleDocumentClick = event => {
			if (event.defaultPrevented || !isPlainLeftClick(event)) {
				return;
			}

			const anchor = event.target.closest && event.target.closest('a[href]');
			if (
				!anchor ||
				anchor.hasAttribute('download') ||
				(anchor.target && anchor.target.toLowerCase() !== '_self') ||
				anchor.getAttribute('rel') === 'external'
			) {
				return;
			}

			const href = anchor.getAttribute('href');
			if (!href) {
				return;
			}

			let destination;
			try {
				destination = new URL(anchor.href, window.location.href);
			} catch {
				return;
			}

			if (
				destination.origin !== window.location.origin ||
				!['http:', 'https:'].includes(destination.protocol)
			) {
				return;
			}

			event.preventDefault();
			history.push(
				`${destination.pathname}${destination.search}${destination.hash}`
			);
		};

		document.addEventListener('click', handleDocumentClick);
		return () => document.removeEventListener('click', handleDocumentClick);
	}, [history]);

	const goBack = useCallback(() => {
		if (routeStackRef.current.index > 0) {
			history.goBack();
		}
	}, [history]);

	const value = useMemo(() => ({ canGoBack, goBack }), [canGoBack, goBack]);

	return (
		<WebsiteNavigationContext.Provider value={value}>
			{children}
		</WebsiteNavigationContext.Provider>
	);
};

const PageBackButton = ({ className = '' }) => {
	const { canGoBack, goBack } = useContext(WebsiteNavigationContext);

	if (!canGoBack) {
		return null;
	}

	return (
		<button
			type="button"
			className={`cg-page-back-button${className ? ` ${className}` : ''}`}
			onClick={goBack}
			aria-label="Go back to the previous page"
			title="Back"
		>
			<svg
				width="18"
				height="18"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				strokeLinejoin="round"
				aria-hidden="true"
			>
				<polyline points="15 18 9 12 15 6" />
			</svg>
		</button>
	);
};

export default PageBackButton;
